import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  Text,
  VStack,
  Flex,
  Icon,
  Button,
  Badge,
  Image
} from '@chakra-ui/react';
import { FiArrowRight } from 'react-icons/fi';
import { FaClock, FaImage } from 'react-icons/fa';
import { motion } from 'framer-motion';
import Header from '../layout/Header';
import Footer from '../layout/Footer';
import axiosInstance from '../services/axiosInstance';
import CobraSpinner from '../shared/CobraSpinner';

const MotionBox = motion(Box);

const NiveauPacks = () => {
  const { niveauId } = useParams();
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [niveau, setNiveau] = useState(null);
  const [loading, setLoading] = useState(true);

  // Theme Constants
  const brandYellow = '#F2C919';
  const lightBg = 'gray.50';
  const primaryText = '#2C3E50';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [niveauRes, packsRes] = await Promise.all([
          axiosInstance.get(`/niveaux/${niveauId}`),
          axiosInstance.get('/packs', { params: { niveau: niveauId, activeOnly: true } })
        ]);
        setNiveau(niveauRes.data.data || niveauRes.data);
        const packsData = packsRes.data.packs || packsRes.data.data || packsRes.data;
        setPacks(Array.isArray(packsData) ? packsData : []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [niveauId]);

  if (loading) {
    return (
      <Flex h="100vh" align="center" justify="center" dir="rtl" bg={lightBg}>
        <VStack spacing={6}>
          <CobraSpinner size="xl" color={brandYellow} thickness="4px" />
          <Text color={primaryText} fontFamily="Tajawal, sans-serif" fontWeight="700" fontSize="lg">
            جاري تحميل الباقات...
          </Text>
        </VStack>
      </Flex>
    );
  }

  return (
    <Box dir="rtl" bg={lightBg} minH="100vh" color={primaryText} overflowX="hidden">
      <Header />

      <Box pt={{ base: 32, md: 40 }} pb={{ base: 12, md: 16 }} position="relative" zIndex={1}>
        <Container maxW="1400px">
          <MotionBox
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Button
              leftIcon={<FiArrowRight />}
              variant="link"
              color="gray.500"
              _hover={{ color: brandYellow }}
              mb={8}
              onClick={() => navigate(-1)}
              fontFamily="Tajawal, sans-serif"
              fontSize="lg"
            >
              العودة إلى المستويات
            </Button>
            <VStack align="flex-start" spacing={4}>
              <Badge
                bg={`${brandYellow}20`}
                color={primaryText}
                px={6}
                py={2}
                borderRadius="full"
                fontSize="md"
                fontFamily="Tajawal, sans-serif"
                fontWeight="800"
              >
                عروض المستوى
              </Badge>
              <Heading size="3xl" fontFamily="Tajawal, sans-serif" fontWeight="900" lineHeight="1.2" color={primaryText}>
                باقات {niveau?.name}
              </Heading>
              <Text fontSize="xl" color="gray.600" fontFamily="Tajawal, sans-serif">
                اختر الباقة المناسبة للبدء في رحلة التعلم والتفوق.
              </Text>
            </VStack>
          </MotionBox>
        </Container>
      </Box>

      <Container maxW="1400px" py={10} position="relative" zIndex={1}>
        {packs.length === 0 ? (
          <Flex direction="column" align="center" justify="center" py={32} bg="white" borderRadius="3xl" border="1px dashed" borderColor="gray.300" boxShadow="sm">
             <Icon as={FaClock} boxSize={20} color="gray.300" mb={6} />
             <Text fontSize="2xl" color="gray.500" fontFamily="Tajawal, sans-serif" fontWeight="800">
               لا توجد باقات متاحة حالياً لهذا المستوى
             </Text>
          </Flex>
        ) : (
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={8} justifyItems="center">
            {packs.map((pack, index) => {
              const imageUrl = pack.imageUrl ? (pack.imageUrl.startsWith('http') ? pack.imageUrl : `${axiosInstance.defaults.baseURL.replace('/api', '')}${pack.imageUrl}`) : null;

              return (
                <MotionBox
                  key={pack._id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1, type: "spring", bounce: 0.4 }}
                  whileHover={{ scale: 1.05, y: -10 }}
                  onClick={() => navigate(`/pack/${pack._id}`)}
                  cursor="pointer"
                  h="100%"
                  w="100%"
                >
                  <Box
                    bg="white"
                    borderRadius="3xl"
                    overflow="hidden"
                    position="relative"
                    border="2px solid"
                    borderColor={pack.isPopular ? brandYellow : "transparent"}
                    transition="all 0.4s"
                    h="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    boxShadow="0 20px 40px rgba(0,0,0,0.08)"
                    aspectRatio={4/5}
                  >
                    {imageUrl ? (
                      <Image src={imageUrl} alt={pack.nameAr || pack.name} w="100%" h="100%" objectFit="cover" />
                    ) : (
                      <VStack spacing={4} p={6} textAlign="center" bg="gray.100" w="100%" h="100%" justify="center">
                        <Icon as={FaImage} boxSize={16} color="gray.300" />
                        <Heading size="md" color={primaryText} fontFamily="Tajawal, sans-serif">{pack.nameAr || pack.name}</Heading>
                        <Text fontSize="sm" color="gray.500" fontFamily="Tajawal, sans-serif">اضغط للتفاصيل</Text>
                      </VStack>
                    )}

                    {pack.isPopular && (
                      <Badge
                        position="absolute"
                        top={4}
                        right={4}
                        bg={brandYellow}
                        color="black"
                        px={4}
                        py={1}
                        borderRadius="full"
                        fontWeight="900"
                        fontSize="sm"
                        fontFamily="Tajawal, sans-serif"
                        boxShadow="lg"
                      >
                        الأكثر طلباً
                      </Badge>
                    )}
                  </Box>
                </MotionBox>
              )
            })}
          </SimpleGrid>
        )}
      </Container>
      <Footer />
    </Box>
  );
};

export default NiveauPacks;
